import { useCallback, useEffect, useRef } from "react";
import correctAudio from '../assets/sounds/correct_answer.mp3';
import incorrectAudio from '../assets/sounds/wrong_answer.mp3';

export const useAudioManager = (questionaudio, questionKey) => {
  const audioRef = useRef(null);
  const currentPlayingAudioRef = useRef(null);
  const hasPlayedForQuestionRef = useRef(null);
  const audioSource = Array.isArray(questionaudio)
    ? questionaudio.find((source) => typeof source === "string" && source.trim())
    : typeof questionaudio === "string"
      ? questionaudio.trim()
      : null;

  const stopAllAudio = useCallback(() => {
    if (currentPlayingAudioRef.current) {
      currentPlayingAudioRef.current.pause();
      currentPlayingAudioRef.current.currentTime = 0;
      currentPlayingAudioRef.current = null;
    }
  }, []);

  const playAudio = useCallback((audioElement) => {
    stopAllAudio();
    if (!audioElement) {
      return Promise.resolve(false);
    }

    currentPlayingAudioRef.current = audioElement;

    try {
      const playResult = audioElement.play();
      if (playResult && typeof playResult.then === "function") {
        return playResult.then(() => true).catch(() => false);
      }
      return Promise.resolve(true);
    } catch {
      return Promise.resolve(false);
    }
  }, [stopAllAudio]);

  useEffect(() => {
    if (audioSource) {
      audioRef.current = new Audio(audioSource);
      audioRef.current.volume = 1;
      audioRef.current.playbackRate = 0.9;
      audioRef.current.preload = "auto";
      audioRef.current.load?.();
    }

    return () => {
      // Stop and clean up audio when questionaudio changes or component unmounts
      stopAllAudio();
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [audioSource, stopAllAudio]);

  useEffect(() => {
    if (hasPlayedForQuestionRef.current === questionKey) {
      return;
    }

    const audioElement = audioRef.current;
    if (!audioElement) {
      return;
    }

    let cancelled = false;
    let interactionListenersAttached = false;

    const markPlayedOnSuccess = (didPlay) => {
      if (!cancelled && didPlay) {
        hasPlayedForQuestionRef.current = questionKey;
        removeInteractionListeners();
      }
    };

    const attemptPlayback = () => {
      if (
        cancelled ||
        hasPlayedForQuestionRef.current === questionKey ||
        audioRef.current !== audioElement
      ) {
        return;
      }

      audioElement.currentTime = 0;
      audioElement.playbackRate = 0.9;
      playAudio(audioElement).then(markPlayedOnSuccess);
    };

    const retryAfterInteraction = () => {
      attemptPlayback();
    };

    function removeInteractionListeners() {
      if (!interactionListenersAttached || typeof window === "undefined") return;
      window.removeEventListener("pointerdown", retryAfterInteraction, true);
      window.removeEventListener("keydown", retryAfterInteraction, true);
      interactionListenersAttached = false;
    }

    attemptPlayback();

    // Browser autoplay policies can reject playback after the asynchronous V2
    // detail request. Retry once the learner next interacts with the page.
    if (typeof window !== "undefined") {
      window.addEventListener("pointerdown", retryAfterInteraction, true);
      window.addEventListener("keydown", retryAfterInteraction, true);
      interactionListenersAttached = true;
    }

    return () => {
      cancelled = true;
      removeInteractionListeners();
      // Stop audio when questionKey changes (e.g. skip) or component unmounts
      stopAllAudio();
    };
  }, [audioSource, playAudio, questionKey, stopAllAudio]);

  const handleAudioReplay = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.playbackRate = 0.9;
      playAudio(audioRef.current);
    }
  };

  const playFeedbackAudio = (isCorrect) => {
    const feedbackAudio = new Audio(isCorrect ? correctAudio : incorrectAudio);
    playAudio(feedbackAudio);
  };

  return {
    handleAudioReplay,
    playFeedbackAudio,
    stopAllAudio
  };
};
